import { get, has, filter, cloneDeep } from 'lodash'
import type { Context as TreeContext } from 'tree-crawl'
import crawl from 'tree-crawl'
import type { PageDefinition, PageType } from '@wix/wix-data-client-common'
import {
  viewerTypes,
  PAGE_TYPES,
  ROUTER_DATASET,
  pagelinkFieldCanBeSorted,
} from '@wix/wix-data-client-common'
import type { FieldMap } from '../../../presets/types'
import type { FieldSpec } from '@wix/wix-data-schema-types'
import type {
  Connection,
  SerializedCompStructure,
} from '@wix/document-services-types'
import type { DataBindingController } from './edit-page-data-fixers/types'
import type { InstalledAppIds } from '../../../../types'
import type { ConnectionConfig } from '@wix/wix-data-client-types'

type Definition = PageDefinition['definition']
type Component = SerializedCompStructure
type Context = TreeContext<Component>
type Modifier = (component: Component, context: Context) => void

const CLASSIC_TITLE_TEXT_PLACEHOLDER = 'Item List'
const STUDIO_TITLE_TEXT_PLACEHOLDER = 'My Items'

const getComponents = (component: Component) => component.components || []

const getMobileComponents = (component: Component) =>
  component.mobileComponents || component.components || []

const traverse = (root: Definition, modifiers: Modifier[]) => {
  const iteratee = (node: Component, context: Context) =>
    modifiers.forEach(fn => fn(node, context))

  root.components.forEach(component =>
    crawl(component, iteratee, {
      getChildren: getComponents,
    }),
  )

  const mobileComponents = root.mobileComponents || []
  mobileComponents.forEach(component =>
    crawl(component, iteratee, {
      getChildren: getMobileComponents,
    }),
  )
}

const isRouterDataset = (component: Component) =>
  component.componentType === 'platform.components.AppController' &&
  get(component, 'data.controllerType') === ROUTER_DATASET

const isRouterDatasetWithCustomConnections = (
  component: Component,
): component is DataBindingController =>
  isRouterDataset(component) && has(component, 'custom.relatedConnections')

const isConnectedComponent = (component: Component) =>
  has(component, 'connections.items[1].config')

const requiresNonEmptyField = (key: string) => ['$text', 'src'].includes(key)

const forEachRelatedConnection = (
  controllerComponent: DataBindingController,
  fn: ({
    componentId,
    connection,
  }: {
    componentId: string
    connection: Connection
  }) => void,
) => {
  Object.entries(controllerComponent.custom.relatedConnections).forEach(
    ([componentId, connections]) => {
      const [connection] = connections as Connection[]
      fn({ componentId, connection })
    },
  )
}

const createIsNonEmptyFieldFn =
  (fieldMap: FieldMap) => (property: { fieldName: string }) =>
    Boolean(fieldMap[property.fieldName])

const hasNecessaryFields = (
  fieldMap: FieldMap,
  properties: ConnectionConfig['properties'],
) => {
  const isNonEmptyField = createIsNonEmptyFieldFn(fieldMap)

  const requiredFields = filter(properties, (_, key) =>
    requiresNonEmptyField(key),
  )

  return requiredFields.every(isNonEmptyField)
}

const hasAtLeastOneNonEmptyField = (
  fieldMap: FieldMap,
  properties: ConnectionConfig['properties'],
) => {
  const isNonEmptyField = createIsNonEmptyFieldFn(fieldMap)

  return filter(properties, isNonEmptyField).length >= 1
}

const removeComponent = (context: Context) => {
  context.parent?.components?.splice(context.index, 1)
  context.remove()
}

const updateFieldNames = (
  fieldMap: FieldMap,
  properties: Required<ConnectionConfig>['properties'],
) =>
  Object.keys(properties).forEach(key => {
    const property = properties[key]
    const newFieldName = fieldMap[property.fieldName]
    if (newFieldName) {
      property.fieldName = newFieldName
    } else {
      delete properties[key]
    }
  })

const fixComponentConnection = (
  fieldMap: FieldMap,
  component: Component,
  context: Context,
) => {
  const config = JSON.parse(component.connections.items[1].config)

  if (config.properties) {
    if (hasNecessaryFields(fieldMap, config.properties)) {
      if (hasAtLeastOneNonEmptyField(fieldMap, config.properties)) {
        updateFieldNames(fieldMap, config.properties)
        component.connections.items[1].config = JSON.stringify(config)
      } else {
        component.connections.items.pop()
      }
    } else {
      removeComponent(context)
    }
  }
}

const fixControllerConnections = (
  fieldMap: FieldMap,
  component: DataBindingController,
) => {
  forEachRelatedConnection(
    component,
    ({
      componentId,
      connection,
    }: {
      componentId: string
      connection: Connection
    }) => {
      if (has(connection, 'config.properties')) {
        const properties = connection.config.properties
        if (
          hasAtLeastOneNonEmptyField(fieldMap, properties) &&
          hasNecessaryFields(fieldMap, properties)
        ) {
          updateFieldNames(fieldMap, properties)
        } else {
          delete component.custom.relatedConnections[componentId]
        }
      }
    },
  )
}

const fixConnections =
  (fieldMap: FieldMap) => (component: Component, context: Context) => {
    if (isRouterDatasetWithCustomConnections(component)) {
      fixControllerConnections(fieldMap, component)
    }

    if (isConnectedComponent(component)) {
      fixComponentConnection(fieldMap, component, context)
    }
  }

const updateConnectionLinkField =
  (pageLinkFieldKey: string) =>
  ({ connection }: { connection: Connection }) => {
    if (has(connection, 'config.properties.link')) {
      connection.config.properties.link.fieldName = pageLinkFieldKey
    }
  }

const fixConnectedComponentLinkField = (
  pageLinkFieldKey: string,
  component: Component,
) => {
  if (isConnectedComponent(component)) {
    const config = JSON.parse(component.connections.items[1].config)

    if (has(config, 'properties.link')) {
      config.properties.link.fieldName = pageLinkFieldKey
      component.connections.items[1].config = JSON.stringify(config)
    }
  }
}

const fixLinks = (pageLinkFieldKey: string) => (component: Component) => {
  if (isRouterDatasetWithCustomConnections(component)) {
    forEachRelatedConnection(
      component,
      updateConnectionLinkField(pageLinkFieldKey),
    )
  }

  fixConnectedComponentLinkField(pageLinkFieldKey, component)
}

const updateInstalledAppIds =
  (installedAppIds: InstalledAppIds) => (component: Component) => {
    if (component.componentType === viewerTypes.TPAWidget) {
      const applicationId = installedAppIds.get(component.data.appDefinitionId)
      component.data.applicationId = String(applicationId)
    }
  }

const updateRouterDatasetName =
  (datasetName: string) => (component: Component) => {
    if (isRouterDataset(component)) {
      component.data.name = datasetName
    }
  }

const setCorrectRouterId = ({
  page,
  routerId,
}: {
  page: Definition
  routerId: string
}): Definition => {
  const stringifiedPage = JSON.stringify(page)
  const pageWithUpdatedRouterId = stringifiedPage.replace(
    /"routerId":"[^"]+"/g,
    `"routerId":"${routerId}"`,
  )
  return JSON.parse(pageWithUpdatedRouterId)
}

const removeComponentsWithActions =
  (actions: string[], controller: DataBindingController) =>
  (component: Component, context: Context) => {
    if (!controller || !component.id) {
      return
    }
    const connections = controller.custom.relatedConnections
    const connectedComponent = connections[component.id]?.find(
      (comp: Connection) => comp.controllerRef.id === controller.id,
    )
    if (!connectedComponent) {
      return
    }
    if (actions.includes(connectedComponent?.config?.events?.onClick?.action)) {
      removeComponent(context)
      delete connections[component.id]
    }
  }

const getDataBindingController = (initialDefinition: Definition) => {
  const searchForController = (
    component: Component,
  ): DataBindingController | null => {
    if (component.data?.applicationId === 'dataBinding') {
      return component as DataBindingController
    }

    if (component.components) {
      for (const subComponent of component.components) {
        const controller = searchForController(subComponent)
        if (controller) {
          return controller
        }
      }
    }

    return null
  }

  for (const component of initialDefinition.components) {
    const controller = searchForController(component)
    if (controller) {
      return controller
    }
  }

  return null
}

const modifyTitleComponentText =
  (collectionName: string) => (component: Component) => {
    const titlePlaceholderRegex = new RegExp(
      `>(${CLASSIC_TITLE_TEXT_PLACEHOLDER}|${STUDIO_TITLE_TEXT_PLACEHOLDER})<`,
    )
    if (
      component.data?.text &&
      titlePlaceholderRegex.test(component.data.text)
    ) {
      component.data.text = component.data.text.replace(
        titlePlaceholderRegex,
        `>${collectionName} List<`,
      )
    }
  }

export const modifyPageDefinition = ({
  initialDefinition,
  modifiers,
}: {
  initialDefinition: Definition
  modifiers: Modifier[]
}) => {
  const definition = cloneDeep(initialDefinition)

  traverse(definition, modifiers)

  return definition
}

export const modifyBlankPresetPageDefinition = ({
  initialDefinition,
  fieldMap,
  routerDatasetName,
  pageLinkFields,
  pageType,
  collectionName,
  isFromExistingCollection,
}: {
  initialDefinition: Definition
  fieldMap: FieldMap
  routerDatasetName: string
  pageLinkFields: FieldSpec[]
  pageType: PageType
  collectionName: string
  isFromExistingCollection?: boolean
}) => {
  const modifiers: Modifier[] = [
    updateRouterDatasetName(routerDatasetName),
    fixConnections(fieldMap),
  ]

  const shouldModifyTitleComponent =
    pageType === PAGE_TYPES.COLLECTION && isFromExistingCollection

  if (shouldModifyTitleComponent) {
    modifiers.push(modifyTitleComponentText(collectionName))
  }

  const dataBindingController = !pageLinkFields.every(pagelinkFieldCanBeSorted)
    ? getDataBindingController(initialDefinition)
    : null

  if (dataBindingController) {
    modifiers.push(
      removeComponentsWithActions(
        ['nextDynamicPage', 'previousDynamicPage'],
        dataBindingController,
      ),
    )
  }

  return modifyPageDefinition({ initialDefinition, modifiers })
}

export const modifyRegularPresetPageDefinition = ({
  initialDefinition,
  pageLinkFieldKey,
  routerDatasetName,
  routerId,
  installedAppIds,
}: {
  initialDefinition: Definition
  pageLinkFieldKey: string
  routerDatasetName: string
  routerId: string
  installedAppIds: InstalledAppIds
}) => {
  const definitionWithFixedRouterId = setCorrectRouterId({
    page: initialDefinition,
    routerId,
  })

  const modifiers: Modifier[] = [
    updateRouterDatasetName(routerDatasetName),
    fixLinks(pageLinkFieldKey),
  ]

  if (installedAppIds.size > 0) {
    modifiers.push(updateInstalledAppIds(installedAppIds))
  }

  return modifyPageDefinition({
    initialDefinition: definitionWithFixedRouterId,
    modifiers,
  })
}
